import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clipboard, Users, HardDrive, FileText, BadgeCheck, BookOpen, BarChart2, LayoutGrid, Settings, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usuariosAPI } from '../services/api';
import { LOGIN_CENTRAL_URL } from '../config';
import LogoApp from './LogoApp';

const Tarjeta = ({ icono: Icono, etiqueta, onClick, externo }) => (
  <button
    type="button"
    onClick={onClick}
    title={etiqueta}
    className="group flex items-center gap-2.5 min-w-0 px-2.5 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/40 dark:hover:bg-gray-700/60 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
  >
    <span className="w-8 h-8 shrink-0 flex items-center justify-center">{Icono}</span>
    <span className="flex-1 min-w-0 text-[13px] font-medium text-gray-800 dark:text-gray-200 leading-tight line-clamp-2">{etiqueta}</span>
    {externo && <ExternalLink className="w-3 h-3 shrink-0 text-gray-300 group-hover:text-blue-500" />}
  </button>
);

const MarcoIcono = ({ children }) => (
  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">{children}</span>
);

const REJILLA = 'grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-2';

/**
 * Accesos directos de la parte superior del dashboard de staff y admin: primero los módulos del portal
 * y después las aplicaciones del holding (las mismas que aparecen en «Accesos Directos» del menú).
 */
export default function AccesosRapidos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [apps, setApps] = useState([]);

  useEffect(() => {
    let cancelado = false;
    const pedir = isAdmin ? usuariosAPI.getPermisosCatalogo().then((r) => (r.data?.apps || []).filter((a) => a.activa)) : usuariosAPI.getMisApps().then((r) => r.data || []);
    pedir.then((lista) => { if (!cancelado) setApps(lista.filter((a) => a.codigo !== 'helpdesk')); }).catch(() => { if (!cancelado) setApps([]); });
    return () => { cancelado = true; };
  }, [isAdmin]);

  const modulos = useMemo(() => [
    { id: 'tickets', etiqueta: 'Tickets', icono: <MarcoIcono><Clipboard className="w-4 h-4" /></MarcoIcono>, to: '/tickets' },
    { id: 'nomina', etiqueta: 'Nómina', icono: <MarcoIcono><Users className="w-4 h-4" /></MarcoIcono>, to: '/usuarios?filtro=all' },
    { id: 'activos', etiqueta: 'Activos', icono: <MarcoIcono><HardDrive className="w-4 h-4" /></MarcoIcono>, to: '/activos' },
    { id: 'actas', etiqueta: 'Actas', icono: <MarcoIcono><FileText className="w-4 h-4" /></MarcoIcono>, to: '/gestion-actas' },
    { id: 'paz', etiqueta: 'Paz y Salvo', icono: <MarcoIcono><BadgeCheck className="w-4 h-4" /></MarcoIcono>, to: isAdmin ? '/pazysalvo-admin' : '/pazysalvo' },
    { id: 'conocimiento', etiqueta: 'Base de Conocimiento', icono: <MarcoIcono><BookOpen className="w-4 h-4" /></MarcoIcono>, to: '/base-conocimiento' },
    { id: 'reportes', etiqueta: 'Reportes', icono: <MarcoIcono><BarChart2 className="w-4 h-4" /></MarcoIcono>, to: '/reportes' },
    ...(isAdmin ? [
      { id: 'aplicaciones', etiqueta: 'Aplicaciones', icono: <MarcoIcono><LayoutGrid className="w-4 h-4" /></MarcoIcono>, to: '/aplicaciones' },
      { id: 'configuracion', etiqueta: 'Configuración', icono: <MarcoIcono><Settings className="w-4 h-4" /></MarcoIcono>, to: '/configuracion' },
    ] : []),
  ], [isAdmin]);

  return (
    <section aria-label="Accesos directos" className="space-y-3">
      <div>
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">Módulos</h2>
        <div className={REJILLA}>
          {modulos.map((m) => <Tarjeta key={m.id} icono={m.icono} etiqueta={m.etiqueta} onClick={() => navigate(m.to)} />)}
        </div>
      </div>

      {apps.length > 0 && (
        <div>
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">Aplicaciones</h2>
          <div className={REJILLA}>
            {apps.map((a) => (
              <Tarjeta
                key={a.codigo}
                externo
                icono={<LogoApp codigo={a.codigo} tam="w-8 h-8" />}
                etiqueta={a.nombre}
                onClick={() => window.open(`${LOGIN_CENTRAL_URL}/?app=${encodeURIComponent(a.codigo)}`, '_blank', 'noopener,noreferrer')}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
